"use client";

import { useSelectedLayoutSegments } from "next/navigation";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { useGailDock } from "@/contexts/chat/GailDockContext";
import { useOrgSites } from "@/contexts/sites/OrgSitesContext";
import {
  NavSection,
  ROUTE_REGISTRY,
  RouteEntry,
  translateTags,
} from "@/data/routeRegistry";
import { Permissions, RoleData, UserRoles, GailProductRoles } from "@/enums/user";
import { useAssistantShortcutHint } from "@/hooks/useAssistantShortcutHint";
import { useBrandConfig } from "@/hooks/useBrandConfig";
import { useEodReportAlert } from "@/hooks/useEodReportAlert";
import { useOrg } from "@/hooks/useOrg";
import { usePermissions } from "@/hooks/usePermissions";
import type { GlobalNavItemType, GlobalNavSectionType } from "@/types/common/library";
import { canAccessNavItem } from "@/utils/authz/navAccess";
import { GetGailGPTBrandName } from "@/utils/chat";
import { BASE_SUPPORT_URL } from "@/utils/common/baseUrls";
import {
  canSeeReportingNav,
  getReportingNavHref,
} from "@/utils/reporting/access";
import { sitesNavSection, withSitesNavSection } from "@/utils/sites/nav";

/**
 * Section configuration for building nav sections
 */
const SECTION_CONFIG: Record<
  NavSection,
  {
    nameKey?: string; // Translation key for section name (undefined = no header)
    useCommonNamespace?: boolean; // Use "common" namespace instead of "routes"
  }
> = {
  [NavSection.Dashboard]: { nameKey: "dataAndAnalytics" },
  [NavSection.GailGpt]: { nameKey: undefined }, // Name is dynamic (brand-based)
  [NavSection.Communications]: { nameKey: "communications" },
  [NavSection.General]: { nameKey: "general" },
  [NavSection.Resources]: { nameKey: undefined }, // Bottom section has no name
};

/**
 * Order of sections when the dock feature flag is on.
 */
const SECTION_ORDER_DOCK: NavSection[] = [
  NavSection.GailGpt,
  NavSection.Dashboard,
  NavSection.Communications,
  NavSection.General,
  NavSection.Resources,
];

/** Pre-dock section order. */
const SECTION_ORDER_LEGACY: NavSection[] = [
  NavSection.Dashboard,
  NavSection.GailGpt,
  NavSection.Communications,
  NavSection.General,
  NavSection.Resources,
];

/**
 * Check if a URL segment is active
 */
const isSegmentActive = (
  segments: string[],
  activeSegments: string[] | undefined,
  position: number = 0
): boolean => {
  if (!activeSegments || activeSegments.length === 0) {
    // Empty activeSegments means active only when no segments (dashboard)
    return segments.length === 0;
  }

  return activeSegments.some(
    (segment) => segments.length > position && segments[position] === segment
  );
};

/**
 * Hook to derive navigation sections from the route registry.
 * Handles permissions and translations.
 */
export function useNavSections() {
  const segments = useSelectedLayoutSegments();
  const { role, productRolesState } = useAuthContext();
  const { productAccess, ui } = useOrg();
  const { config: brandConfig } = useBrandConfig(ui.brand);
  const { canAccess, canAccessAny, permissions } = usePermissions();
  const { hasAlert: hasEodReportAlert } = useEodReportAlert();
  const {
    isGailGptDockEnabled,
    isDockAvailable,
    isOpen: isDockOpen,
    toggleDock,
  } = useGailDock();

  const { t } = useTranslation("routes");
  const { t: ct } = useTranslation("common");
  const assistantShortcutHint = useAssistantShortcutHint();
  const { sites } = useOrgSites();
  const hasSites = sites.length > 0;
  const isReportUser =
    productRolesState?.roles?.includes(GailProductRoles.ReportUser) ?? false;
  const gailAgentEnabled = productAccess.gailAgentEnabled ?? false;

  /**
   * Build a GlobalNavItemType from a RouteEntry
   */
  const buildNavItem = (route: RouteEntry): GlobalNavItemType | null => {
    const nav = route.nav;
    if (!nav) return null;

    // Determine the label
    let label: string;
    if (nav.labelKey === "reporting") {
      // Reporting uses common namespace
      label = ct("reporting");
    } else {
      label = t(nav.labelKey);
    }

    // Determine the href (support link uses BASE_SUPPORT_URL)
    const href =
      nav.labelKey === "support"
        ? BASE_SUPPORT_URL
        : route.path === "/reporting"
          ? getReportingNavHref(gailAgentEnabled, isReportUser)
          : route.path;

    // Check if active
    const isActive =
      nav.labelKey === "support"
        ? false
        : isSegmentActive(segments, nav.activeSegments, nav.activePosition ?? 0);

    // Translate tags to current language
    const translatedTags = translateTags(route.tagKeys, t);

    const hasAlert = route.path === "/reporting" ? hasEodReportAlert : false;

    return {
      id: nav.tourId,
      icon: nav.icon,
      label,
      href,
      isActive,
      target: nav.target,
      dataType: nav.dataType,
      additionalDataTypes: nav.additionalDataTypes,
      levels: nav.levels,
      dataAttributes: nav.dataAttributes,
      tags: translatedTags,
      hasAlert,
    };
  };

  const effectiveSection = (route: RouteEntry): NavSection | undefined =>
    !isGailGptDockEnabled && route.path === "/skills"
      ? NavSection.GailGpt
      : route.nav?.section;

  /**
   * Build all navigation sections
   */
  const sections = useMemo<GlobalNavSectionType[]>(() => {
    const navSections: GlobalNavSectionType[] = [];

    const sectionOrder = isGailGptDockEnabled ? SECTION_ORDER_DOCK : SECTION_ORDER_LEGACY;

    for (const sectionKey of sectionOrder) {
      // Skip GailGPT section if user doesn't have access
      if (sectionKey === NavSection.GailGpt && !productAccess.userCanUseGailGpt) {
        continue;
      }

      const config = SECTION_CONFIG[sectionKey];

      // Get routes for this section
      const sectionRoutes = ROUTE_REGISTRY.filter(
        (route) => effectiveSection(route) === sectionKey && route.nav
      ).sort((a, b) => (a.nav?.order ?? 99) - (b.nav?.order ?? 99));

      // Build nav items
      const items: GlobalNavItemType[] = sectionRoutes
        .map(buildNavItem)
        .filter((item): item is GlobalNavItemType => item !== null);

      // Filter by permissions. Reporting additionally needs the product axis: `report.read` says
      // the role may read reports, but which reporting surface exists depends on org Agent / the
      // ReportUser seat.
      const accessibleItems = items.filter((item) => {
        if (item.dataType === RoleData.Reporting) {
          return canSeeReportingNav(
            gailAgentEnabled,
            isReportUser,
            canAccess(RoleData.Reporting, Permissions.Read)
          );
        }
        return canAccessNavItem(item, canAccessAny);
      });

      if (accessibleItems.length === 0) continue;

      // Determine section name
      let sectionName: string | undefined;
      let sectionItems = accessibleItems;
      if (sectionKey === NavSection.GailGpt) {
        const brandName = GetGailGPTBrandName(ui.brand, t, brandConfig);

        if (isDockAvailable) {
          sectionName = undefined;
          sectionItems = [
            {
              id: "tour-nav-gailgpt",
              icon: "wand-magic-sparkles",
              label: brandName,
              onClick: toggleDock,
              isActive: isDockOpen,
              dataType: RoleData.GPT,
              dataAttributes: { "data-tour": "nav-gailgpt" },
              variant: "assistant",
              shortcutHint: assistantShortcutHint,
            },
          ];
        } else {
          sectionName = brandName;
        }
      } else {
        sectionName = config.nameKey ? t(config.nameKey) : undefined;
      }

      // Determine position
      const position = sectionKey === NavSection.Resources ? "bottom" : "top";

      navSections.push({
        name: sectionName,
        sectionKey,
        position,
        items: sectionItems,
      });
    }

    return withSitesNavSection(navSections, sitesNavSection(hasSites, segments, t("sites")));
  }, [
    segments,
    hasSites,
    productAccess.userCanUseGailGpt,
    productAccess.gailAgentEnabled,
    isReportUser,
    ui.brand,
    brandConfig,
    permissions,
    hasEodReportAlert,
    isGailGptDockEnabled,
    isDockAvailable,
    isDockOpen,
    toggleDock,
    assistantShortcutHint,
    t,
    ct,
  ]);

  /**
   * Side nav message based on user role
   */
  const sideNavMessage = useMemo(() => {
    if (role === UserRoles.Integration) {
      return t("integrationRole");
    }
    return "";
  }, [role, t]);

  return {
    sections,
    sideNavMessage,
  };
}
